import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

interface EmissionEntry {
  id: number;
  scope: string;
  activity_type: string;
  activity_description?: string;
  quantity: number;
  unit: string;
  emission_factor?: number;
  co2_equivalent: number;
  status: string;
  is_estimated: boolean;
  estimation_method?: string;
  created_at: string;
  reporting_year?: number;
  energy_source?: string;
  supplier?: string;
}

interface Scope2EntriesTableProps {
  entries: EmissionEntry[];
  isLoading: boolean;
}

type FilterType = 'all' | 'measured' | 'estimated';
type SortField = 'co2_equivalent' | 'activity_type' | 'energy_source';
type SortOrder = 'asc' | 'desc';

export function Scope2EntriesTable({ entries, isLoading }: Scope2EntriesTableProps) {
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [sortField, setSortField] = useState<SortField>('co2_equivalent');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter and sort entries
  const filteredAndSortedEntries = useMemo(() => {
    let filtered = entries;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(entry => {
        switch (filterType) {
          case 'measured':
            return !entry.is_estimated;
          case 'estimated':
            return entry.is_estimated;
          default:
            return true;
        }
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(entry =>
        entry.activity_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.activity_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.energy_source?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.supplier?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort entries
    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'co2_equivalent') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [entries, filterType, sortField, sortOrder, searchTerm]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const getEntryType = (entry: EmissionEntry) => {
    return entry.is_estimated ? 'Estimated' : 'Measured';
  };

  const getEntryTypeColor = (entry: EmissionEntry) => {
    return entry.is_estimated ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800';
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Existing Scope 2 Entries</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-gray-500 text-center py-8">Loading entries...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Existing Scope 2 Entries</span>
          <span className="text-sm font-normal text-gray-500">
            {filteredAndSortedEntries.length} of {entries.length} entries
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search by activity, energy source, or supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filter by Type */}
            <div className="min-w-48">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as FilterType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="measured">Measured</option>
                <option value="estimated">Estimated</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        {filteredAndSortedEntries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th 
                    className="border border-gray-200 px-4 py-3 text-left cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('activity_type')}
                  >
                    Activity Type {getSortIcon('activity_type')}
                  </th>
                  <th 
                    className="border border-gray-200 px-4 py-3 text-left cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('energy_source')}
                  >
                    Energy Source {getSortIcon('energy_source')}
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-left">Supplier</th>
                  <th className="border border-gray-200 px-4 py-3 text-left">Type</th>
                  <th className="border border-gray-200 px-4 py-3 text-right">Quantity</th>
                  <th className="border border-gray-200 px-4 py-3 text-right">Unit</th>
                  <th 
                    className="border border-gray-200 px-4 py-3 text-right cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('co2_equivalent')}
                  >
                    CO₂e (kg) {getSortIcon('co2_equivalent')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="border border-gray-200 px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {entry.activity_type || 'Unknown Activity'}
                      </div>
                    </td>
                    <td className="border border-gray-200 px-4 py-3">
                      <div className="text-sm text-gray-700">
                        {entry.energy_source || 'N/A'}
                      </div>
                    </td>
                    <td className="border border-gray-200 px-4 py-3">
                      <div className="text-sm text-gray-600">
                        {entry.supplier || 'N/A'}
                      </div>
                    </td>
                    <td className="border border-gray-200 px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEntryTypeColor(entry)}`}>
                        {getEntryType(entry)}
                      </span>
                    </td>
                    <td className="border border-gray-200 px-4 py-3 text-right">
                      <div className="font-medium">
                        {entry.quantity?.toLocaleString() || '0'}
                      </div>
                    </td>
                    <td className="border border-gray-200 px-4 py-3 text-right">
                      <div className="text-sm text-gray-600">
                        {entry.unit || 'N/A'}
                      </div>
                    </td>
                    <td className="border border-gray-200 px-4 py-3 text-right">
                      <div className="font-semibold text-blue-700">
                        {entry.co2_equivalent?.toFixed(2) || '0.00'} kg
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {searchTerm || filterType !== 'all' 
              ? 'No entries match your current filters.' 
              : 'No entries found. Add your first Scope 2 emission entry.'
            }
          </div>
        )}

        {/* Summary */}
        {filteredAndSortedEntries.length > 0 && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Total CO₂e: <span className="font-semibold text-blue-700">
                  {filteredAndSortedEntries.reduce((sum, entry) => sum + (entry.co2_equivalent || 0), 0).toFixed(2)} kg
                </span>
              </div>
              <div className="text-sm text-gray-600">
                Average per entry: <span className="font-semibold">
                  {(filteredAndSortedEntries.reduce((sum, entry) => sum + (entry.co2_equivalent || 0), 0) / filteredAndSortedEntries.length).toFixed(2)} kg
                </span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
